/**
 * useWorkspace — the data layer for the Knowledge Workspace.
 *
 * Editing model: the open note lives in a mutable ref (`noteRef`). Typing
 * mutates block text in place and only marks the note dirty — no React
 * re-render, so the caret never jumps. Structural changes (add/move/delete
 * block, type changes) bump `rev` to re-render the block list.
 *
 * Save model — honest, never fabricated:
 *   dirty → (debounce 800ms) → saving → saved@time | error | conflict
 * "Saved" is only shown after the server confirms the write. A version number
 * travels with every save; a 409 surfaces a conflict banner with both
 * resolutions instead of silently overwriting.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { workspaceAPI } from '../../services/api'
import { stripHtml } from '../../utils/format'

const SAVE_DEBOUNCE_MS = 800

const RICH_TEXT_TYPES = new Set(['p', 'h1', 'h2', 'h3', 'bullet', 'numbered', 'todo', 'quote'])
const blockPlain = (b) =>
  RICH_TEXT_TYPES.has(b.type) ? stripHtml(b.text) : (b.text || (b.table ? b.table.rows.flat().join(' ') : ''))

export function newBlock(type = 'p', extra = {}) {
  return {
    id: `b_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`,
    type,
    ...(type === 'table'
      ? { table: { rows: [['', '', ''], ['', '', ''], ['', '', '']] } }
      : type === 'divider' || type === 'attachment'
        ? {}
        : { text: '' }),
    ...(type === 'todo' ? { checked: false } : {}),
    ...extra,
  }
}

export default function useWorkspace() {
  const [index, setIndex] = useState([])           // note metas
  const [collections, setCollections] = useState([])
  const [extraCollections, setExtraCollections] = useState([])  // created this session, not yet holding a note
  const [trash, setTrash] = useState([])
  const [view, setView] = useState('all')          // 'all' | collection | 'trash'
  const [activeId, setActiveId] = useState(null)
  const [rev, setRev] = useState(0)                // structural revision of the open note
  const [loading, setLoading] = useState(true)
  const [noteLoading, setNoteLoading] = useState(false)
  const [loadError, setLoadError] = useState(null)
  const [saveState, setSaveState] = useState({ status: 'idle', at: null, message: null })
  const [conflict, setConflict] = useState(null)   // server copy on 409

  const noteRef = useRef(null)        // the open note document (mutable)
  const dirtyRef = useRef(false)
  const savingRef = useRef(false)
  const timerRef = useRef(null)
  const saveStateRef = useRef(saveState)
  saveStateRef.current = saveState

  const bump = useCallback(() => setRev((r) => r + 1), [])

  // ── Index ──────────────────────────────────────────────────────────────────

  const loadIndex = useCallback(async () => {
    try {
      setLoadError(null)
      const { data } = await workspaceAPI.listNotes()
      setIndex(data.notes || [])
      setCollections(data.collections || [])
      return data.notes || []
    } catch (err) {
      setLoadError(err.response?.data?.message || err.message || 'Could not load workspace')
      return []
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { loadIndex() }, [loadIndex])

  const metaFromDoc = (doc) => {
    const firstText = (doc.blocks || [])
      .map((b) => (typeof b.text === 'string' ? blockPlain(b) : ''))
      .find((t) => t && t.trim()) || ''
    const plainWords = (doc.blocks || []).map(blockPlain).join(' ').trim()
    return {
      id: doc.id,
      title: doc.title || '',
      collection: doc.collection || 'Inbox',
      pinned: !!doc.pinned,
      createdAt: doc.createdAt,
      updatedAt: doc.updatedAt,
      version: doc.version,
      excerpt: firstText.slice(0, 160),
      words: plainWords ? plainWords.split(/\s+/).length : 0,
      blockCount: (doc.blocks || []).length,
      attachmentCount: (doc.attachments || []).length,
    }
  }

  const upsertMeta = useCallback((doc) => {
    const meta = metaFromDoc(doc)
    setIndex((prev) => {
      const rest = prev.filter((n) => n.id !== meta.id)
      const next = [meta, ...rest]
      next.sort((a, b) => (b.pinned - a.pinned) || (a.updatedAt < b.updatedAt ? 1 : -1))
      return next
    })
    setCollections((prev) => (prev.includes(meta.collection) ? prev : [...prev, meta.collection].sort()))
  }, [])

  // ── Saving ─────────────────────────────────────────────────────────────────

  const doSave = useCallback(async () => {
    const note = noteRef.current
    if (!note || !dirtyRef.current || savingRef.current) return
    savingRef.current = true
    dirtyRef.current = false
    setSaveState({ status: 'saving', at: null, message: null })
    try {
      const { data } = await workspaceAPI.updateNote(note.id, {
        title: note.title,
        collection: note.collection,
        pinned: note.pinned,
        blocks: note.blocks,
        version: note.version,
      })
      // Adopt server bookkeeping only — never replace blocks mid-typing.
      note.version = data.version
      note.updatedAt = data.updatedAt
      upsertMeta({ ...data, blocks: note.blocks, attachments: note.attachments })
      setSaveState({ status: 'saved', at: new Date(), message: null })
    } catch (err) {
      dirtyRef.current = true
      if (err.response?.status === 409) {
        setConflict(err.response.data.serverNote || null)
        setSaveState({ status: 'conflict', at: null, message: 'This note changed elsewhere' })
      } else {
        setSaveState({
          status: 'error', at: null,
          message: err.response?.data?.message || err.message || 'Save failed',
        })
      }
    } finally {
      savingRef.current = false
      if (dirtyRef.current && saveStateRef.current.status === 'saving') {
        // typed during the save — go again
        scheduleSave()
      }
    }
  }, [upsertMeta])   // eslint-disable-line react-hooks/exhaustive-deps

  const scheduleSave = useCallback(() => {
    clearTimeout(timerRef.current)
    timerRef.current = setTimeout(doSave, SAVE_DEBOUNCE_MS)
  }, [doSave])

  const markDirty = useCallback(() => {
    dirtyRef.current = true
    if (saveStateRef.current.status !== 'dirty' && saveStateRef.current.status !== 'conflict') {
      setSaveState({ status: 'dirty', at: null, message: null })
    }
    scheduleSave()
  }, [scheduleSave])

  const markStructural = useCallback(() => { bump(); markDirty() }, [bump, markDirty])

  const flushSave = useCallback(async () => {
    clearTimeout(timerRef.current)
    if (dirtyRef.current && !savingRef.current) await doSave()
  }, [doSave])

  // Warn before closing the tab with unsaved changes — never lose data silently.
  useEffect(() => {
    const onBeforeUnload = (e) => {
      if (dirtyRef.current || savingRef.current) {
        e.preventDefault()
        e.returnValue = ''
      }
    }
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => window.removeEventListener('beforeunload', onBeforeUnload)
  }, [])

  // ── Conflict resolution ────────────────────────────────────────────────────

  const resolveConflictTheirs = useCallback(() => {
    if (!conflict) return
    noteRef.current = conflict
    dirtyRef.current = false
    setConflict(null)
    setSaveState({ status: 'saved', at: new Date(), message: null })
    bump()
  }, [conflict, bump])

  const resolveConflictMine = useCallback(() => {
    if (!conflict || !noteRef.current) return
    noteRef.current.version = conflict.version   // overwrite deliberately, with the user's consent
    setConflict(null)
    dirtyRef.current = true
    doSave()
  }, [conflict, doSave])

  // ── Note lifecycle ─────────────────────────────────────────────────────────

  const openNote = useCallback(async (id) => {
    if (id === noteRef.current?.id) return
    await flushSave()
    setConflict(null)
    setNoteLoading(true)
    setActiveId(id)
    try {
      const { data } = await workspaceAPI.getNote(id)
      noteRef.current = data
      dirtyRef.current = false
      setSaveState({ status: 'saved', at: data.updatedAt ? new Date(data.updatedAt) : null, message: null })
      bump()
    } catch (err) {
      noteRef.current = null
      setActiveId(null)
      setSaveState({ status: 'error', at: null, message: 'Could not open note' })
    } finally {
      setNoteLoading(false)
    }
  }, [flushSave, bump])

  const closeNote = useCallback(async () => {
    await flushSave()
    noteRef.current = null
    setActiveId(null)
    setConflict(null)
    bump()
  }, [flushSave, bump])

  const createNote = useCallback(async (collection, title = '') => {
    await flushSave()
    const col = collection && collection !== 'all' && collection !== 'trash' ? collection : 'Inbox'
    const { data } = await workspaceAPI.createNote({ collection: col, title })
    noteRef.current = data
    dirtyRef.current = false
    setActiveId(data.id)
    setConflict(null)
    setSaveState({ status: 'saved', at: new Date(), message: null })
    upsertMeta(data)
    setExtraCollections((prev) => prev.filter((c) => c !== col))
    bump()
    return data
  }, [flushSave, upsertMeta, bump])

  const deleteNote = useCallback(async (id) => {
    if (noteRef.current?.id === id) {
      clearTimeout(timerRef.current)
      dirtyRef.current = false
      noteRef.current = null
      setActiveId(null)
      setConflict(null)
    }
    await workspaceAPI.deleteNote(id)
    setIndex((prev) => prev.filter((n) => n.id !== id))
    bump()
  }, [bump])

  const loadTrash = useCallback(async () => {
    const { data } = await workspaceAPI.listTrash()
    setTrash(data.notes || [])
  }, [])

  const restoreNote = useCallback(async (id) => {
    const { data } = await workspaceAPI.restoreNote(id)
    setTrash((prev) => prev.filter((n) => n.id !== id))
    upsertMeta(data)
    return data
  }, [upsertMeta])

  const destroyNote = useCallback(async (id) => {
    await workspaceAPI.destroyNote(id)
    setTrash((prev) => prev.filter((n) => n.id !== id))
  }, [])

  // Pin / move from the list (works without opening the note)
  const patchMeta = useCallback(async (meta, patch) => {
    try {
      const { data } = await workspaceAPI.updateNote(meta.id, { ...patch, version: meta.version })
      if (noteRef.current?.id === meta.id) {
        Object.assign(noteRef.current, patch, { version: data.version, updatedAt: data.updatedAt })
        bump()
      }
      upsertMeta(data)
    } catch (err) {
      if (err.response?.status === 409) await loadIndex()   // refresh stale meta, fail closed
      else throw err
    }
  }, [upsertMeta, loadIndex, bump])

  // ── Attachments ────────────────────────────────────────────────────────────

  /** Flush pending edits, upload, adopt the new version, return attachment meta. */
  const uploadAttachment = useCallback(async (file) => {
    const note = noteRef.current
    if (!note) throw new Error('No open note')
    await flushSave()
    const { data } = await workspaceAPI.uploadAttachment(note.id, file)
    note.attachments = note.attachments || []
    note.attachments.push(data.attachment)
    note.version = data.version
    return data.attachment
  }, [flushSave])

  const createCollection = useCallback((name) => {
    const clean = String(name || '').trim().slice(0, 60)
    if (!clean) return
    if (!collections.includes(clean) && !extraCollections.includes(clean)) {
      setExtraCollections((prev) => [...prev, clean].sort())
    }
    setView(clean)
  }, [collections, extraCollections])

  return {
    // data
    index, collections, extraCollections, trash, view, activeId,
    note: noteRef.current, rev, loading, noteLoading, loadError, saveState, conflict,
    // actions
    setView, openNote, closeNote, createNote, deleteNote,
    loadTrash, restoreNote, destroyNote, patchMeta, createCollection,
    markDirty, markStructural, flushSave, uploadAttachment,
    resolveConflictTheirs, resolveConflictMine,
    loadIndex,
  }
}
